import { createApi } from '@reduxjs/toolkit/dist/query/react';

import {
  GetUserProfileRes,
  UpdateUserDataSubscriptionBody,
  UserLoginReqBody,
  UserRegisterReqBody,
  UserRegisterResponse,
} from '@/modules/Login/types';

import { ApiTagsEnum } from '@/enums/apiTags.enum';
import { StorageKeysEnum } from '@/enums/storageKeys.enum';

import {
  GetOneGameRes,
  GetPageResponse,
  GetRecentGamesRes,
  GetTodayGamesRes,
} from '@/types/domain';

import { baseQueryWithRedirect } from '@/utils/api';
import { AUTH_API_URL, TENANT } from '@/utils/constants';
import { getStorageItem } from '@/utils/localStorage';

export const api = createApi({
  reducerPath: 'mainApi',
  tagTypes: Object.values(ApiTagsEnum),
  baseQuery: baseQueryWithRedirect,
  endpoints: (builder) => ({
    getTodayGames: builder.query<GetTodayGamesRes, { jwt: string }>({
      query: () => '/games-today',
      providesTags: [ApiTagsEnum.GET_TODAY_GAMES],
    }),
    getRecentGames: builder.query<GetRecentGamesRes, { jwt: string }>({
      query: () => '/games-recent',
      providesTags: [ApiTagsEnum.GET_RECENT_GAMES],
    }),
    getOneGame: builder.query<GetOneGameRes, { id: string; jwt: string }>({
      query: ({ id }) => `/games/${id}`,
      providesTags: [ApiTagsEnum.GET_ONE_GAME],
    }),
    signUp: builder.mutation<{ success: boolean }, { id: number }>({
      query: ({ id }) => {
        const url = `/games/${id}/join`;
        return { url, method: 'POST' };
      },
    }),
    generateJWTByToken: builder.mutation<{ token: string }, { token: string }>({
      query: (body) => ({ url: '/login', method: 'POST', body }),
    }),
    getRules: builder.query<GetPageResponse, { lang: string }>({
      query: ({ lang }) => `/page/rules?lang=${lang}`,
    }),
    getContestRules: builder.query<
      GetPageResponse,
      { id: string; lang: string }
    >({
      query: ({ id, lang }) => `/page/contest:${id}?lang=${lang}`,
    }),
    getAbbrRules: builder.query<GetPageResponse, { lang: string }>({
      query: ({ lang }) => `/page/abbr-rules?lang=${lang}`,
    }),

    registration: builder.mutation<UserRegisterResponse, UserRegisterReqBody>({
      query: (body) => ({
        url: `${AUTH_API_URL}/auth/signup`,
        method: 'POST',
        body,
      }),
    }),

    login: builder.mutation<UserRegisterResponse, UserLoginReqBody>({
      query: (body) => ({
        url: `${AUTH_API_URL}/auth/login`,
        method: 'POST',
        body,
      }),
    }),

    updateUserDataSubscription: builder.mutation<
      { success: boolean },
      UpdateUserDataSubscriptionBody
    >({
      query: (body) => {
        const jwt = getStorageItem<string>(StorageKeysEnum.usersJwt);
        return {
          url: `${AUTH_API_URL}/user/notification-preferences`,
          method: 'PUT',
          body,
          headers: { Authorization: `Bearer ${jwt}`, 'X-TENANT-ID': TENANT },
        };
      },
    }),

    getUserProfile: builder.query<GetUserProfileRes, void>({
      query: () => {
        const jwt = getStorageItem<string>(StorageKeysEnum.jwt);
        return {
          url: `${AUTH_API_URL}/user/me`,
          headers: {
            Authorization: `Bearer ${jwt}`,
            'X-TENANT-ID': TENANT,
          },
        };
      },
      providesTags: [ApiTagsEnum.USER],
    }),

    getTmpToken: builder.query<GetUserProfileRes, void>({
      query: () => {
        const userJwt = getStorageItem<string>(StorageKeysEnum.jwt);
        return {
          url: `${AUTH_API_URL}/user/tmp_token`,
          headers: {
            Authorization: `Bearer ${userJwt}`,
            'X-TENANT-ID': TENANT,
          },
        };
      },
      providesTags: [ApiTagsEnum.USER],
    }),
  }),
});

export const {
  useGetTodayGamesQuery,
  useGetRecentGamesQuery,
  useGetOneGameQuery,
  useLazyGetOneGameQuery,
  useSignUpMutation,
  useGenerateJWTByTokenMutation,

  useGetRulesQuery,
  useGetContestRulesQuery,
  useGetAbbrRulesQuery,

  useRegistrationMutation,
  useLoginMutation,

  useUpdateUserDataSubscriptionMutation,
  useGetUserProfileQuery,
  useGetTmpTokenQuery,
} = api;
